import { API_URL as BASE_URL } from '../../lib/config';
const API_URL = `${BASE_URL}/platform-admin`;

export interface AuditLog {
  id: string;
  platformAdminId: string;
  organizationId: string | null;
  action: string;
  resource: string;
  resourceId: string | null;
  previousValue: any;
  newValue: any;
  ipAddress: string | null;
  userAgent: string | null;
  metadata: any;
  createdAt: string;
  platformAdmin: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
  };
  organization: {
    id: string;
    name: string;
    status: string;
  } | null;
}

export interface PaginatedAuditLogs {
  data: AuditLog[];
  total: number;
  page: number;
  pageSize: number;
}

export interface GetAuditLogsParams {
  platformAdminId?: string;
  organizationId?: string;
  action?: string;
  resource?: string;
  startDate?: string;
  endDate?: string;
  skip?: number;
  take?: number;
}

export interface AuditLogStats {
  total: number;
  last24Hours: number;
  byAction: Array<{ action: string; count: number }>;
  byResource: Array<{ resource: string; count: number }>;
}

export const auditLogsService = {
  /**
   * Get audit logs with filtering and pagination
   */
  async getLogs(token: string, params?: GetAuditLogsParams): Promise<PaginatedAuditLogs> {
    const queryParams = new URLSearchParams();

    if (params?.platformAdminId) queryParams.append('platformAdminId', params.platformAdminId);
    if (params?.organizationId) queryParams.append('organizationId', params.organizationId);
    if (params?.action) queryParams.append('action', params.action);
    if (params?.resource) queryParams.append('resource', params.resource);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString());
    if (params?.take !== undefined) queryParams.append('take', params.take.toString());

    const url = `${API_URL}/audit-logs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch audit logs');
    }

    return response.json();
  },

  /**
   * Get audit log by ID
   */
  async getLogById(token: string, id: string): Promise<AuditLog> {
    const response = await fetch(`${API_URL}/audit-logs/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch audit log');
    }

    return response.json();
  },

  /**
   * Get audit log statistics
   */
  async getStats(token: string, startDate?: string, endDate?: string): Promise<AuditLogStats> {
    const queryParams = new URLSearchParams();
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);

    const url = `${API_URL}/audit-logs/statistics/summary${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch statistics');
    }

    return response.json();
  },

  /**
   * Get unique actions
   */
  async getUniqueActions(token: string): Promise<string[]> {
    const response = await fetch(`${API_URL}/audit-logs/filters/actions`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch actions');
    }

    return response.json();
  },

  /**
   * Get unique resources
   */
  async getUniqueResources(token: string): Promise<string[]> {
    const response = await fetch(`${API_URL}/audit-logs/filters/resources`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch resources');
    }

    return response.json();
  },

  /**
   * Get activity for a specific platform admin
   */
  async getAdminActivity(token: string, platformAdminId: string, limit?: number): Promise<AuditLog[]> {
    const queryParams = new URLSearchParams();
    if (limit) queryParams.append('limit', limit.toString());

    const url = `${API_URL}/audit-logs/admin/${platformAdminId}/activity${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch admin activity');
    }

    return response.json();
  },

  /**
   * Get activity for a specific organization
   */
  async getOrganizationActivity(token: string, organizationId: string, limit?: number): Promise<AuditLog[]> {
    const queryParams = new URLSearchParams();
    if (limit) queryParams.append('limit', limit.toString());

    const url = `${API_URL}/audit-logs/organization/${organizationId}/activity${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch organization activity');
    }

    return response.json();
  },
};
